import React, { useMemo, useState } from 'react';

import { formatHotkeyLabel } from '../constants';

const RESOURCE_KEYS = [
  'energy',
  'alloys',
  'research',
  'food',
  'credits',
] as const;

const BASE_TRADE_MAINTENANCE_RATE = 0.12;

const DEFAULT_LOG_CAPACITY_FLOOR = 0;

const RESOURCE_LABELS: Record<SpaceResource, string> = {
  energy: 'Energy',
  alloys: 'Alloys',
  research: 'Research',
  food: 'Food',
  credits: 'Credits',
};

const FOCUS_BASE_OUTPUT: Record<ColonyFocus, ResourceAmount> = {
  industry: { energy: 5, alloys: 12, credits: 3 },
  science: { energy: 4, research: 14, credits: 2 },
  agriculture: { energy: 3, food: 18, credits: 2 },
  logistics: { energy: 6, alloys: 4, credits: 8 },
};

export type SpaceResource = (typeof RESOURCE_KEYS)[number];

type ResourceMap = Record<SpaceResource, number>;

type ResourceAmount = Partial<Record<SpaceResource, number>>;

type ColonyFocus = 'industry' | 'science' | 'agriculture' | 'logistics';

interface BuildingDefinition {
  id: string;
  name: string;
  description: string;
  category: 'Habitation' | 'Energy' | 'Industry' | 'Science' | 'Logistics';
  baseCost: ResourceAmount;
  baseOutput: ResourceAmount;
  focusMultipliers?: Partial<Record<ColonyFocus, number>>;
  populationBonus?: number;
  stabilityBonus?: number;
  logisticBonus?: number;
  requires?: string[];
}

interface UpgradeDefinition {
  id: string;
  name: string;
  description: string;
  cost: ResourceAmount;
  effects: {
    globalOutputBonus?: number;
    tradeEfficiencyBonus?: number;
    stabilityBonus?: number;
    focusMultiplier?: Partial<Record<ColonyFocus, number>>;
    populationGrowthBonus?: number;
    logisticsBonus?: number;
    maintenanceMultiplier?: number;
  };
  requires?: string[];
}

interface Colony {
  id: string;
  name: string;
  focus: ColonyFocus;
  population: number;
  stability: number;
  baseLogisticsCapacity: number;
  buildings: Record<string, number>;
}

interface TradeRoute {
  id: string;
  fromColonyId: string;
  toColonyId: string;
  resource: SpaceResource;
  capacity: number;
  efficiency: number;
}

export interface GalaxyState {
  year: number;
  globalStockpile: ResourceMap;
  colonies: Colony[];
  upgrades: Record<string, boolean>;
  tradeRoutes: TradeRoute[];
  logs: string[];
  nextTradeRouteId: number;
}

interface AggregateUpgradeEffects {
  globalOutputBonus: number;
  tradeEfficiencyBonus: number;
  stabilityBonus: number;
  focusMultiplier: Partial<Record<ColonyFocus, number>>;
  populationGrowthBonus: number;
  logisticsBonus: number;
  maintenanceMultiplier: number;
}

interface TradeSummary {
  exports: ResourceMap;
  imports: ResourceMap;
  totalExportCapacity: number;
}

interface TopColonyHighlight {
  name: string;
  value: number;
}

interface BuildingRowProps {
  colony: Colony;
  building: BuildingDefinition;
  cost: ResourceAmount;
  onConstruct: (colonyId: string, buildingId: string) => void;
  canAfford: boolean;
  requirementsMet: boolean;
}

interface SpaceCivModeProps {
  prestigePoints: number;
  prestigeCount: number;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  galaxyState: GalaxyState;
  setGalaxyState: React.Dispatch<React.SetStateAction<GalaxyState>>;
}

interface NewRouteFormState {
  fromColonyId: string;
  toColonyId: string;
  resource: SpaceResource;
  capacity: number;
}

const BUILDING_DEFINITIONS: BuildingDefinition[] = [
  {
    id: 'habitat-ring',
    name: 'Habitat Ring',
    description:
      'Expands orbital living space with biodome gardens, supporting sustained growth and morale.',
    category: 'Habitation',
    baseCost: { alloys: 40, food: 20, energy: 15, credits: 25 },
    baseOutput: { food: 5, credits: 2 },
    focusMultipliers: { agriculture: 0.1, logistics: 0.05 },
    populationBonus: 3,
    stabilityBonus: 1,
  },
  {
    id: 'solar-array',
    name: 'Solar Array Bloom',
    description:
      'Dense lattice of sail arrays that track the local star, boosting clean energy output for the entire system.',
    category: 'Energy',
    baseCost: { alloys: 25, credits: 30, energy: 10 },
    baseOutput: { energy: 14 },
    focusMultipliers: { industry: 0.05, logistics: 0.08 },
  },
  {
    id: 'orbital-forge',
    name: 'Orbital Nanoforge',
    description:
      'Autonomous fabrication swarm that refines raw ore streams into alloys and components.',
    category: 'Industry',
    baseCost: { energy: 30, alloys: 55, credits: 20 },
    baseOutput: { alloys: 14, credits: 2 },
    focusMultipliers: { industry: 0.18 },
    requires: ['planetary-automation'],
  },
  {
    id: 'quantum-labs',
    name: 'Quantum Research Nexus',
    description:
      'Entangled research grid coordinating multi-world science teams with instant data replication.',
    category: 'Science',
    baseCost: { energy: 35, research: 45, credits: 25 },
    baseOutput: { research: 16, energy: 4 },
    focusMultipliers: { science: 0.22 },
    stabilityBonus: 1,
  },
  {
    id: 'stellar-port',
    name: 'Stellar Trade Port',
    description:
      'Gateway docks, courier hubs, and customs arrays expanding inter-colony trade throughput.',
    category: 'Logistics',
    baseCost: { energy: 40, alloys: 35, credits: 40 },
    baseOutput: { credits: 10, energy: 3 },
    focusMultipliers: { logistics: 0.2 },
    stabilityBonus: 1,
    logisticBonus: 10,
  },
  {
    id: 'terraform-core',
    name: 'Terraforming Core',
    description:
      'Climate regulators and biosphere tuning to stabilize frontier worlds for large populations.',
    category: 'Habitation',
    baseCost: { food: 40, energy: 35, alloys: 30, credits: 30 },
    baseOutput: { food: 12 },
    focusMultipliers: { agriculture: 0.2 },
    populationBonus: 4,
    stabilityBonus: 2,
    requires: ['harmonic-terraforming'],
  },
];

const UPGRADE_DEFINITIONS: UpgradeDefinition[] = [
  {
    id: 'galactic-bureaucracy',
    name: 'Galactic Bureaucracy',
    description:
      'Unified administrative mesh that streamlines taxation, coordination, and policy execution.',
    cost: { credits: 200, research: 120, alloys: 80 },
    effects: {
      globalOutputBonus: 0.1,
      stabilityBonus: 1,
    },
  },
  {
    id: 'quantum-trade-network',
    name: 'Quantum Trade Network',
    description:
      'Entangled ledgers and courier beacons that increase route efficiency and safe throughput.',
    cost: { energy: 220, research: 140, credits: 140 },
    effects: {
      tradeEfficiencyBonus: 0.15,
      logisticsBonus: 8,
      maintenanceMultiplier: 0.85,
    },
  },
  {
    id: 'planetary-automation',
    name: 'Planetary Automation',
    description:
      'Autonomous industrial overseers that maximize output with minimal oversight.',
    cost: { energy: 200, alloys: 160, research: 180 },
    effects: {
      focusMultiplier: { industry: 0.22 },
      populationGrowthBonus: 1,
    },
  },
  {
    id: 'pan-galactic-forum',
    name: 'Pan-Galactic Forum',
    description:
      'Shared civic network promoting cohesion, dispute resolution, and culture exchange.',
    cost: { credits: 210, food: 160, research: 90 },
    effects: {
      stabilityBonus: 2,
    },
  },
  {
    id: 'stellar-cartography-ai',
    name: 'Stellar Cartography AI',
    description:
      'Predictive navigation cores chart optimal trade lanes and relay windows.',
    cost: { research: 220, energy: 120, credits: 120 },
    effects: {
      focusMultiplier: { science: 0.18, logistics: 0.1 },
      globalOutputBonus: 0.05,
    },
    requires: ['galactic-bureaucracy'],
  },
  {
    id: 'harmonic-terraforming',
    name: 'Harmonic Terraforming',
    description:
      'Macro-scale resonant terraformers stabilize weather and ecosystems in months.',
    cost: { food: 260, energy: 150, alloys: 120, research: 140 },
    effects: {
      focusMultiplier: { agriculture: 0.24 },
      populationGrowthBonus: 2,
    },
  },
  {
    id: 'void-mercantile-charter',
    name: 'Void Mercantile Charter',
    description:
      'Trade guild accords reduce tariffs and streamline contract law between colonies.',
    cost: { credits: 260, alloys: 140 },
    effects: {
      maintenanceMultiplier: 0.75,
      stabilityBonus: 1,
    },
  },
];

const createEmptyResourceMap = (): ResourceMap => ({
  energy: 0,
  alloys: 0,
  research: 0,
  food: 0,
  credits: 0,
});

const cloneResourceMap = (map: ResourceMap): ResourceMap => ({ ...map });

const roundTo = (value: number, decimals: number = 2): number => {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
};

const scaleResourceMap = (map: ResourceMap, factor: number): ResourceMap => {
  const next = createEmptyResourceMap();
  for (const key of RESOURCE_KEYS) {
    next[key] = roundTo(map[key] * factor);
  }
  return next;
};

const addResourceMaps = (
  lhs: ResourceMap,
  rhs: ResourceMap | ResourceAmount
): ResourceMap => {
  const next = cloneResourceMap(lhs);
  for (const key of RESOURCE_KEYS) {
    const addition =
      (rhs as ResourceMap)[key] ?? (rhs as ResourceAmount)[key] ?? 0;
    next[key] += addition;
  }
  return next;
};

const subtractResourceAmount = (
  map: ResourceMap,
  cost: ResourceAmount
): ResourceMap => {
  const next = cloneResourceMap(map);
  for (const key of RESOURCE_KEYS) {
    next[key] -= cost[key] ?? 0;
  }
  return next;
};

const hasSufficientResources = (
  stockpile: ResourceMap,
  cost: ResourceAmount
): boolean => {
  return RESOURCE_KEYS.every(key => (cost[key] ?? 0) <= stockpile[key]);
};

const toResourceMap = (amount: ResourceAmount): ResourceMap => {
  const next = createEmptyResourceMap();
  for (const key of RESOURCE_KEYS) {
    next[key] = amount[key] ?? 0;
  }
  return next;
};

const getBuildingDefinition = (
  buildingId: string
): BuildingDefinition | undefined => {
  return BUILDING_DEFINITIONS.find(building => building.id === buildingId);
};

const getUpgradeDefinition = (
  upgradeId: string
): UpgradeDefinition | undefined => {
  return UPGRADE_DEFINITIONS.find(upgrade => upgrade.id === upgradeId);
};

const calculateBuildingCost = (
  baseCost: ResourceAmount,
  level: number
): ResourceAmount => {
  const multiplier = 1 + (level - 1) * 0.45;
  const cost: ResourceAmount = {};
  for (const key of RESOURCE_KEYS) {
    const base = baseCost[key];
    if (base) {
      cost[key] = roundTo(base * multiplier);
    }
  }
  return cost;
};

const calculateAggregateUpgradeEffects = (
  upgrades: Record<string, boolean>
): AggregateUpgradeEffects => {
  return UPGRADE_DEFINITIONS.reduce<AggregateUpgradeEffects>(
    (effects, upgrade) => {
      if (!upgrades[upgrade.id]) {
        return effects;
      }

      const next = {
        ...effects,
        focusMultiplier: { ...effects.focusMultiplier },
      };

      if (upgrade.effects.globalOutputBonus) {
        next.globalOutputBonus += upgrade.effects.globalOutputBonus;
      }
      if (upgrade.effects.tradeEfficiencyBonus) {
        next.tradeEfficiencyBonus += upgrade.effects.tradeEfficiencyBonus;
      }
      if (upgrade.effects.stabilityBonus) {
        next.stabilityBonus += upgrade.effects.stabilityBonus;
      }
      if (upgrade.effects.populationGrowthBonus) {
        next.populationGrowthBonus += upgrade.effects.populationGrowthBonus;
      }
      if (upgrade.effects.logisticsBonus) {
        next.logisticsBonus += upgrade.effects.logisticsBonus;
      }
      if (upgrade.effects.maintenanceMultiplier) {
        next.maintenanceMultiplier *= upgrade.effects.maintenanceMultiplier;
      }
      if (upgrade.effects.focusMultiplier) {
        for (const focus of Object.keys(
          upgrade.effects.focusMultiplier
        ) as ColonyFocus[]) {
          const bonus = upgrade.effects.focusMultiplier?.[focus];
          if (bonus) {
            next.focusMultiplier[focus] =
              (next.focusMultiplier[focus] ?? 0) + bonus;
          }
        }
      }
      return next;
    },
    {
      globalOutputBonus: 0,
      tradeEfficiencyBonus: 0,
      stabilityBonus: 0,
      focusMultiplier: {},
      populationGrowthBonus: 0,
      logisticsBonus: 0,
      maintenanceMultiplier: 1,
    }
  );
};

const calculateBuildingSupports = (
  colony: Colony
): { population: number; stability: number; logistics: number } => {
  let population = 0;
  let stability = 0;
  let logistics = 0;

  for (const buildingId of Object.keys(colony.buildings)) {
    const level = colony.buildings[buildingId] ?? 0;
    if (level <= 0) {
      continue;
    }
    const definition = getBuildingDefinition(buildingId);
    if (!definition) {
      continue;
    }

    if (definition.populationBonus) {
      population += definition.populationBonus * level;
    }
    if (definition.stabilityBonus) {
      stability += definition.stabilityBonus * level;
    }
    if (definition.logisticBonus) {
      logistics += definition.logisticBonus * level;
    }
  }

  return { population, stability, logistics };
};

const calculateColonyOutput = (
  colony: Colony,
  effects: AggregateUpgradeEffects
): ResourceMap => {
  let output = createEmptyResourceMap();

  output = addResourceMaps(
    output,
    toResourceMap(FOCUS_BASE_OUTPUT[colony.focus])
  );

  for (const buildingDefinition of BUILDING_DEFINITIONS) {
    const level = colony.buildings[buildingDefinition.id] ?? 0;
    if (level <= 0) {
      continue;
    }

    const baseOutput = scaleResourceMap(
      toResourceMap(buildingDefinition.baseOutput),
      level
    );

    const focusBonus = buildingDefinition.focusMultipliers?.[colony.focus] ?? 0;
    const adjustedOutput = scaleResourceMap(baseOutput, 1 + focusBonus);
    output = addResourceMaps(output, adjustedOutput);
  }

  const focusMultiplier = 1 + (effects.focusMultiplier[colony.focus] ?? 0);
  const totalMultiplier = 1 + effects.globalOutputBonus;
  const combined = focusMultiplier * totalMultiplier;

  return scaleResourceMap(output, combined);
};

const calculateTradeSummary = (
  colonyId: string,
  tradeRoutes: TradeRoute[],
  effects: AggregateUpgradeEffects
): TradeSummary => {
  const exports = createEmptyResourceMap();
  const imports = createEmptyResourceMap();
  let totalExportCapacity = 0;

  for (const route of tradeRoutes) {
    if (route.fromColonyId === colonyId) {
      exports[route.resource] += route.capacity;
      totalExportCapacity += route.capacity;
    }

    if (route.toColonyId === colonyId) {
      const efficiency = Math.min(
        1,
        route.efficiency + effects.tradeEfficiencyBonus
      );
      imports[route.resource] += roundTo(route.capacity * efficiency);
    }
  }

  return { exports, imports, totalExportCapacity };
};

const getColonyLogisticsCapacity = (
  colony: Colony,
  effects: AggregateUpgradeEffects
): number => {
  const buildingSupports = calculateBuildingSupports(colony);
  return (
    colony.baseLogisticsCapacity +
    buildingSupports.logistics +
    effects.logisticsBonus
  );
};

const applyTradeAdjustments = (
  output: ResourceMap,
  tradeSummary: TradeSummary,
  effects: AggregateUpgradeEffects
): { netOutput: ResourceMap; maintenanceCost: number } => {
  const net = createEmptyResourceMap();
  let totalExports = 0;

  for (const key of RESOURCE_KEYS) {
    const exported = tradeSummary.exports[key];
    const imported = tradeSummary.imports[key];
    net[key] = roundTo(output[key] - exported + imported);
    totalExports += exported;
  }

  const maintenanceRate =
    BASE_TRADE_MAINTENANCE_RATE * effects.maintenanceMultiplier;
  const maintenanceCost = roundTo(totalExports * maintenanceRate);
  net.credits = roundTo(net.credits - maintenanceCost);

  return { netOutput: net, maintenanceCost };
};

const formatResourceAmount = (amount: ResourceAmount): string => {
  const parts: string[] = [];
  for (const key of RESOURCE_KEYS) {
    const value = amount[key];
    if (value && Math.abs(value) > 0.05) {
      parts.push(
        `${RESOURCE_LABELS[key]} ${value > 0 ? '+' : ''}${roundTo(value, 2)}`
      );
    }
  }
  return parts.join(' · ');
};

const formatResourceDelta = (map: ResourceMap): string => {
  const parts: string[] = [];
  for (const key of RESOURCE_KEYS) {
    const value = map[key];
    if (Math.abs(value) > 0.05) {
      parts.push(
        `${RESOURCE_LABELS[key]} ${value > 0 ? '+' : ''}${roundTo(value, 1)}`
      );
    }
  }
  return parts.join(' · ');
};

export const calculateInitialGalaxyState = (): GalaxyState => ({
  year: 4103,
  globalStockpile: {
    energy: 950,
    alloys: 720,
    research: 540,
    food: 860,
    credits: 640,
  },
  colonies: [
    {
      id: 'sol',
      name: 'Sol Assembly',
      focus: 'logistics',
      population: 48,
      stability: 86,
      baseLogisticsCapacity: 36,
      buildings: {
        'solar-array': 2,
        'stellar-port': 1,
        'habitat-ring': 1,
      },
    },
    {
      id: 'eden',
      name: 'New Eden',
      focus: 'agriculture',
      population: 32,
      stability: 78,
      baseLogisticsCapacity: 26,
      buildings: {
        'habitat-ring': 2,
        'terraform-core': 1,
      },
    },
    {
      id: 'kepler',
      name: 'Kepler Research Union',
      focus: 'science',
      population: 21,
      stability: 88,
      baseLogisticsCapacity: 22,
      buildings: {
        'quantum-labs': 1,
        'solar-array': 1,
      },
    },
    {
      id: 'forge',
      name: 'Vulcan Forgeworks',
      focus: 'industry',
      population: 27,
      stability: 74,
      baseLogisticsCapacity: 28,
      buildings: {
        'solar-array': 1,
      },
    },
  ],
  upgrades: UPGRADE_DEFINITIONS.reduce<Record<string, boolean>>(
    (acc, upgrade) => {
      acc[upgrade.id] = false;
      return acc;
    },
    {}
  ),
  tradeRoutes: [
    {
      id: 'route-1',
      fromColonyId: 'eden',
      toColonyId: 'sol',
      resource: 'food',
      capacity: 14,
      efficiency: 0.78,
    },
    {
      id: 'route-2',
      fromColonyId: 'sol',
      toColonyId: 'kepler',
      resource: 'energy',
      capacity: 10,
      efficiency: 0.7,
    },
    {
      id: 'route-3',
      fromColonyId: 'forge',
      toColonyId: 'sol',
      resource: 'alloys',
      capacity: 9,
      efficiency: 0.74,
    },
  ],
  logs: [
    'Prestige threshold met – civilization command uplink online.',
    'Colonies synced: Sol Assembly, New Eden, Kepler Research Union, Vulcan Forgeworks.',
  ],
  nextTradeRouteId: 4,
});

const clampNumber = (value: number, min: number, max: number): number => {
  return Math.min(max, Math.max(min, value));
};

const formatPopulation = (populationMillions: number): string => {
  return `${populationMillions.toLocaleString()} M`;
};

const formatStockpile = (stockpile: ResourceMap): string[] => {
  return RESOURCE_KEYS.map(
    key =>
      `${RESOURCE_LABELS[key]}: ${roundTo(stockpile[key], 1).toLocaleString()}`
  );
};

const summarizeTrade = (summary: TradeSummary): string => {
  const fragments: string[] = [];
  for (const key of RESOURCE_KEYS) {
    const exports = summary.exports[key];
    const imports = summary.imports[key];
    if (exports > 0 || imports > 0) {
      fragments.push(
        `${RESOURCE_LABELS[key]} • Exports ${roundTo(exports, 1)} / Imports ${roundTo(imports, 1)}`
      );
    }
  }
  if (fragments.length === 0) {
    return 'No active trade routes';
  }
  return fragments.join(' | ');
};

const BuildingRow: React.FC<BuildingRowProps> = ({
  colony,
  building,
  cost,
  onConstruct,
  canAfford,
  requirementsMet,
}) => {
  const level = colony.buildings[building.id] ?? 0;

  return (
    <div className='space-mode__building-row'>
      <div className='space-mode__building-summary'>
        <strong>{building.name}</strong>
        <span className='space-mode__building-meta'>
          Tier {level}
          {building.requires && building.requires.length > 0
            ? ` • Requires ${building.requires
                .map(id => getUpgradeDefinition(id)?.name ?? id)
                .join(', ')}`
            : ''}
        </span>
        <p>{building.description}</p>
      </div>
      <div className='space-mode__building-actions'>
        <div className='space-mode__building-cost'>
          Cost: {formatResourceAmount(cost)}
        </div>
        <button
          type='button'
          className='space-mode__action-button'
          onClick={() => onConstruct(colony.id, building.id)}
          disabled={!canAfford || !requirementsMet}
        >
          {requirementsMet ? 'Construct' : 'Locked'}
        </button>
      </div>
    </div>
  );
};

export const SpaceCivMode: React.FC<SpaceCivModeProps> = ({
  prestigePoints,
  prestigeCount,
  showSuccess,
  showError,
  galaxyState,
  setGalaxyState,
}) => {
  const [newRouteForm, setNewRouteForm] = useState<NewRouteFormState>(() => ({
    fromColonyId: 'sol',
    toColonyId: 'eden',
    resource: 'credits',
    capacity: 6,
  }));

  const aggregateEffects = useMemo(
    () => calculateAggregateUpgradeEffects(galaxyState.upgrades),
    [galaxyState.upgrades]
  );

  const totalPopulation = useMemo(
    () =>
      galaxyState.colonies.reduce((acc, colony) => acc + colony.population, 0),
    [galaxyState.colonies]
  );

  const totalTradeThroughput = useMemo(
    () =>
      galaxyState.tradeRoutes.reduce((acc, route) => acc + route.capacity, 0),
    [galaxyState.tradeRoutes]
  );

  const handleConstructBuilding = (
    colonyId: string,
    buildingId: string
  ): void => {
    let message: string | null = null;

    setGalaxyState(prev => {
      const colonyIndex = prev.colonies.findIndex(
        colony => colony.id === colonyId
      );
      const buildingDefinition = getBuildingDefinition(buildingId);

      if (colonyIndex === -1 || !buildingDefinition) {
        return prev;
      }

      const colony = prev.colonies[colonyIndex];
      if (!colony) {
        return prev;
      }

      if (
        buildingDefinition.requires &&
        buildingDefinition.requires.some(
          requirement => !prev.upgrades[requirement]
        )
      ) {
        message = null;
        showError(
          `${buildingDefinition.name} is locked. Acquire the prerequisite upgrades first.`
        );
        return prev;
      }

      const currentLevel = colony.buildings[buildingId] ?? 0;
      const nextLevel = currentLevel + 1;
      const cost = calculateBuildingCost(
        buildingDefinition.baseCost,
        nextLevel
      );

      if (!hasSufficientResources(prev.globalStockpile, cost)) {
        message = null;
        showError('Insufficient resources for that construction project.');
        return prev;
      }

      const updatedColony: Colony = {
        ...colony,
        buildings: {
          ...colony.buildings,
          [buildingId]: nextLevel,
        },
      };

      const updatedColonies = [...prev.colonies];
      updatedColonies[colonyIndex] = updatedColony;

      const updatedStockpile = subtractResourceAmount(
        prev.globalStockpile,
        cost
      );

      const nextLogs = [
        `${buildingDefinition.name} upgraded to level ${nextLevel} on ${updatedColony.name}.`,
        ...prev.logs,
      ].slice(DEFAULT_LOG_CAPACITY_FLOOR, DEFAULT_LOG_CAPACITY_FLOOR + 14);

      message = `${buildingDefinition.name} construction complete on ${updatedColony.name}.`;

      return {
        ...prev,
        colonies: updatedColonies,
        globalStockpile: updatedStockpile,
        logs: nextLogs,
      };
    });

    if (message) {
      showSuccess(message);
    }
  };

  const handleActivateUpgrade = (upgradeId: string): void => {
    let message: string | null = null;

    setGalaxyState(prev => {
      if (prev.upgrades[upgradeId]) {
        showError('Upgrade already activated.');
        return prev;
      }

      const definition = getUpgradeDefinition(upgradeId);
      if (!definition) {
        return prev;
      }

      if (
        definition.requires &&
        definition.requires.some(requirement => !prev.upgrades[requirement])
      ) {
        showError('Prerequisite upgrade not yet unlocked.');
        return prev;
      }

      if (!hasSufficientResources(prev.globalStockpile, definition.cost)) {
        showError('Not enough galactic resources to authorize this upgrade.');
        return prev;
      }

      const updatedStockpile = subtractResourceAmount(
        prev.globalStockpile,
        definition.cost
      );

      const nextUpgrades = {
        ...prev.upgrades,
        [upgradeId]: true,
      };

      const nextLogs = [
        `${definition.name} enacted across all colonies.`,
        ...prev.logs,
      ].slice(DEFAULT_LOG_CAPACITY_FLOOR, DEFAULT_LOG_CAPACITY_FLOOR + 14);

      message = `${definition.name} is now active.`;

      return {
        ...prev,
        upgrades: nextUpgrades,
        globalStockpile: updatedStockpile,
        logs: nextLogs,
      };
    });

    if (message) {
      showSuccess(message);
    }
  };

  const handleAdvanceYear = (): void => {
    let yearAdvanced = 0;

    setGalaxyState(prev => {
      const effects = calculateAggregateUpgradeEffects(prev.upgrades);
      const nextStockpile = cloneResourceMap(prev.globalStockpile);
      const year = prev.year + 1;
      const colonyLogs: string[] = [];
      let totalMaintenance = 0;
      let topColony: TopColonyHighlight | null = null;

      const updatedColonies = prev.colonies.map(colony => {
        const output = calculateColonyOutput(colony, effects);
        const tradeSummary = calculateTradeSummary(
          colony.id,
          prev.tradeRoutes,
          effects
        );
        const { maintenanceCost, netOutput } = applyTradeAdjustments(
          output,
          tradeSummary,
          effects
        );
        totalMaintenance += maintenanceCost;

        for (const key of RESOURCE_KEYS) {
          nextStockpile[key] = Math.max(
            0,
            roundTo(nextStockpile[key] + netOutput[key])
          );
        }

        const buildingSupports = calculateBuildingSupports(colony);
        const logisticsCapacity = getColonyLogisticsCapacity(colony, effects);
        const logisticsOveruse = Math.max(
          0,
          tradeSummary.totalExportCapacity - logisticsCapacity
        );

        const foodSupport =
          output.food + tradeSummary.imports.food - tradeSummary.exports.food;
        const growthBase =
          foodSupport / 4 +
          buildingSupports.population +
          effects.populationGrowthBonus;
        const populationDelta = Math.round(growthBase);
        const nextPopulation = Math.max(1, colony.population + populationDelta);

        let stabilityChange =
          buildingSupports.stability +
          effects.stabilityBonus +
          Math.round(tradeSummary.imports.credits / 6);

        if (foodSupport < 0) {
          stabilityChange += Math.floor(foodSupport / 3);
        }
        if (logisticsOveruse > 0) {
          stabilityChange -= Math.ceil(logisticsOveruse / 2);
        }

        const nextStability = clampNumber(
          colony.stability + stabilityChange,
          25,
          100
        );

        const productionSummary = formatResourceDelta(netOutput);
        colonyLogs.push(
          `${colony.name}: ${productionSummary || 'Stable outputs'} • Pop ${nextPopulation}M • Stability ${nextStability}%`
        );

        const colonyValue =
          netOutput.credits + netOutput.energy + netOutput.research;
        if (!topColony || colonyValue > topColony.value) {
          topColony = { name: colony.name, value: colonyValue };
        }

        return {
          ...colony,
          population: nextPopulation,
          stability: nextStability,
        };
      });

      yearAdvanced = year;

      const headerLog = `Galactic Year ${year}: maintenance ${roundTo(
        totalMaintenance,
        2
      ).toLocaleString()} credits • Trade throughput ${roundTo(
        totalTradeThroughput,
        1
      ).toLocaleString()} units.`;

      let highlightLog = 'Colony outputs balanced across the network.';
      if (topColony) {
        const { name, value } = topColony;
        highlightLog = `${name} led contributions with a combined output score of ${roundTo(
          value,
          1
        ).toLocaleString()}.`;
      }

      const nextLogs = [
        headerLog,
        highlightLog,
        ...colonyLogs,
        ...prev.logs,
      ].slice(DEFAULT_LOG_CAPACITY_FLOOR, DEFAULT_LOG_CAPACITY_FLOOR + 16);

      return {
        ...prev,
        year,
        colonies: updatedColonies,
        globalStockpile: nextStockpile,
        logs: nextLogs,
      };
    });

    if (yearAdvanced > 0) {
      showSuccess(`Advanced to Galactic Year ${yearAdvanced}.`);
    }
  };

  const handleTradeFormChange = (
    field: keyof NewRouteFormState,
    value: string | number
  ) => {
    setNewRouteForm(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCreateTradeRoute = (): void => {
    if (newRouteForm.fromColonyId === newRouteForm.toColonyId) {
      showError('Select two different colonies for a new trade route.');
      return;
    }

    let created = false;

    setGalaxyState(prev => {
      const fromColony = prev.colonies.find(
        colony => colony.id === newRouteForm.fromColonyId
      );
      const toColony = prev.colonies.find(
        colony => colony.id === newRouteForm.toColonyId
      );

      if (!fromColony || !toColony) {
        showError('Invalid colony selection.');
        return prev;
      }

      const effects = calculateAggregateUpgradeEffects(prev.upgrades);
      const tradeSummary = calculateTradeSummary(
        fromColony.id,
        prev.tradeRoutes,
        effects
      );
      const logisticsCapacity = getColonyLogisticsCapacity(fromColony, effects);

      if (
        tradeSummary.totalExportCapacity + newRouteForm.capacity >
        logisticsCapacity
      ) {
        showError(
          `${fromColony.name} lacks logistics capacity. Increase infrastructure before adding this route.`
        );
        return prev;
      }

      const newRoute: TradeRoute = {
        id: `route-${prev.nextTradeRouteId}`,
        fromColonyId: fromColony.id,
        toColonyId: toColony.id,
        resource: newRouteForm.resource,
        capacity: newRouteForm.capacity,
        efficiency: 0.72,
      };

      const nextRoutes = [newRoute, ...prev.tradeRoutes];
      const nextLogs = [
        `Trade route established: ${fromColony.name} → ${toColony.name} (${RESOURCE_LABELS[newRoute.resource]} ${newRoute.capacity}/yr).`,
        ...prev.logs,
      ].slice(DEFAULT_LOG_CAPACITY_FLOOR, DEFAULT_LOG_CAPACITY_FLOOR + 14);

      created = true;

      return {
        ...prev,
        tradeRoutes: nextRoutes,
        logs: nextLogs,
        nextTradeRouteId: prev.nextTradeRouteId + 1,
      };
    });

    if (created) {
      showSuccess('Trade route added to the galactic ledger.');
    }
  };

  const handleAdjustTradeCapacity = (routeId: string, delta: number): void => {
    let adjusted = false;

    setGalaxyState(prev => {
      const routeIndex = prev.tradeRoutes.findIndex(
        route => route.id === routeId
      );
      if (routeIndex === -1) {
        return prev;
      }

      const currentRoute = prev.tradeRoutes[routeIndex];
      if (!currentRoute) {
        return prev;
      }
      const newCapacity = clampNumber(currentRoute.capacity + delta, 2, 60);
      if (newCapacity === currentRoute.capacity) {
        return prev;
      }

      const effects = calculateAggregateUpgradeEffects(prev.upgrades);
      const fromColony = prev.colonies.find(
        colony => colony.id === currentRoute.fromColonyId
      );

      if (!fromColony) {
        return prev;
      }

      const tradeSummary = calculateTradeSummary(
        fromColony.id,
        prev.tradeRoutes,
        effects
      );
      const logisticsCapacity = getColonyLogisticsCapacity(fromColony, effects);
      const adjustedExportCapacity =
        tradeSummary.totalExportCapacity - currentRoute.capacity + newCapacity;

      if (adjustedExportCapacity > logisticsCapacity) {
        showError(
          `${fromColony.name} cannot sustain that level of trade throughput without further upgrades.`
        );
        return prev;
      }

      const updatedRoutes = [...prev.tradeRoutes];
      updatedRoutes[routeIndex] = {
        ...currentRoute,
        capacity: newCapacity,
      };

      adjusted = true;

      const nextLogs = [
        `Adjusted ${fromColony.name} → ${currentRoute.toColonyId} trade capacity to ${newCapacity}.`,
        ...prev.logs,
      ].slice(DEFAULT_LOG_CAPACITY_FLOOR, DEFAULT_LOG_CAPACITY_FLOOR + 14);

      return {
        ...prev,
        tradeRoutes: updatedRoutes,
        logs: nextLogs,
      };
    });

    if (adjusted) {
      showSuccess('Trade capacity updated.');
    }
  };

  const handleRemoveTradeRoute = (routeId: string): void => {
    let removed = false;

    setGalaxyState(prev => {
      if (!prev.tradeRoutes.some(route => route.id === routeId)) {
        return prev;
      }

      const nextRoutes = prev.tradeRoutes.filter(route => route.id !== routeId);
      const nextLogs = [
        `Trade route ${routeId} decommissioned.`,
        ...prev.logs,
      ].slice(DEFAULT_LOG_CAPACITY_FLOOR, DEFAULT_LOG_CAPACITY_FLOOR + 14);

      removed = true;

      return {
        ...prev,
        tradeRoutes: nextRoutes,
        logs: nextLogs,
      };
    });

    if (removed) {
      showSuccess('Trade route removed from the network.');
    }
  };

  return (
    <div className='space-mode'>
      <header className='space-mode__header'>
        <div>
          <h1>Galactic Directorate</h1>
          <p>
            Prestige {prestigePoints.toLocaleString()} • Launches{' '}
            {prestigeCount.toLocaleString()} • Galactic Year{' '}
            {galaxyState.year.toLocaleString()}
          </p>
        </div>
        <div className='space-mode__summary-grid'>
          <div>
            <span className='space-mode__summary-label'>Population</span>
            <strong>{formatPopulation(totalPopulation)}</strong>
          </div>
          <div>
            <span className='space-mode__summary-label'>Trade Throughput</span>
            <strong>
              {roundTo(totalTradeThroughput, 1).toLocaleString()} units/yr
            </strong>
          </div>
          <div>
            <span className='space-mode__summary-label'>Active Upgrades</span>
            <strong>
              {Object.values(galaxyState.upgrades).filter(Boolean).length}/
              {UPGRADE_DEFINITIONS.length}
            </strong>
          </div>
          <div>
            <span className='space-mode__summary-label'>Advance Time</span>
            <button
              type='button'
              className='space-mode__primary-button'
              onClick={handleAdvanceYear}
            >
              Advance Year ({formatHotkeyLabel(['Enter'])})
            </button>
          </div>
        </div>
      </header>

      <section className='space-mode__section'>
        <h2>Galactic Stockpile</h2>
        <div className='space-mode__stockpile'>
          {formatStockpile(galaxyState.globalStockpile).map(entry => (
            <span key={entry}>{entry}</span>
          ))}
        </div>
      </section>

      <section className='space-mode__section'>
        <h2>Colony Overview</h2>
        <div className='space-mode__colonies'>
          {galaxyState.colonies.map(colony => {
            const output = calculateColonyOutput(colony, aggregateEffects);
            const tradeSummary = calculateTradeSummary(
              colony.id,
              galaxyState.tradeRoutes,
              aggregateEffects
            );
            const logisticsCapacity = getColonyLogisticsCapacity(
              colony,
              aggregateEffects
            );

            return (
              <article key={colony.id} className='space-mode__colony-card'>
                <header>
                  <h3>{colony.name}</h3>
                  <p>
                    Focus: {colony.focus.toUpperCase()} • Population{' '}
                    {formatPopulation(colony.population)} • Stability{' '}
                    {colony.stability}%
                  </p>
                  <p>
                    Logistics Capacity {roundTo(logisticsCapacity, 1)} • Active
                    Exports {roundTo(tradeSummary.totalExportCapacity, 1)}
                  </p>
                </header>
                <div className='space-mode__colony-production'>
                  <span className='space-mode__summary-label'>
                    Projected Output
                  </span>
                  <p>
                    {formatResourceDelta(output) ||
                      'No current production modifiers.'}
                  </p>
                </div>
                <div className='space-mode__colony-trade'>
                  <span className='space-mode__summary-label'>
                    Trade Summary
                  </span>
                  <p>{summarizeTrade(tradeSummary)}</p>
                </div>
                <div className='space-mode__building-list'>
                  {BUILDING_DEFINITIONS.map(building => {
                    const level = colony.buildings[building.id] ?? 0;
                    const nextLevel = level + 1;
                    const cost = calculateBuildingCost(
                      building.baseCost,
                      nextLevel
                    );
                    const requirementsMet =
                      !building.requires ||
                      building.requires.every(req => galaxyState.upgrades[req]);
                    const canAfford = hasSufficientResources(
                      galaxyState.globalStockpile,
                      cost
                    );
                    return (
                      <BuildingRow
                        key={building.id}
                        colony={colony}
                        building={building}
                        cost={cost}
                        onConstruct={handleConstructBuilding}
                        canAfford={canAfford}
                        requirementsMet={requirementsMet}
                      />
                    );
                  })}
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <section className='space-mode__section'>
        <h2>Galactic Upgrades</h2>
        <div className='space-mode__upgrades'>
          {UPGRADE_DEFINITIONS.map(upgrade => {
            const isUnlocked = galaxyState.upgrades[upgrade.id];
            const requirementsMet =
              !upgrade.requires ||
              upgrade.requires.every(req => galaxyState.upgrades[req]);
            const canAfford = hasSufficientResources(
              galaxyState.globalStockpile,
              upgrade.cost
            );

            return (
              <div key={upgrade.id} className='space-mode__upgrade-card'>
                <h3>{upgrade.name}</h3>
                <p>{upgrade.description}</p>
                <p className='space-mode__upgrade-cost'>
                  Cost: {formatResourceAmount(upgrade.cost)}
                </p>
                <button
                  type='button'
                  className='space-mode__action-button'
                  onClick={() => handleActivateUpgrade(upgrade.id)}
                  disabled={isUnlocked || !requirementsMet || !canAfford}
                >
                  {isUnlocked
                    ? 'Activated'
                    : requirementsMet
                      ? 'Enact Policy'
                      : 'Locked'}
                </button>
              </div>
            );
          })}
        </div>
      </section>

      <section className='space-mode__section'>
        <h2>Trade Network</h2>
        <div className='space-mode__trade-form'>
          <div className='space-mode__trade-fields'>
            <label>
              From Colony
              <select
                value={newRouteForm.fromColonyId}
                onChange={event =>
                  handleTradeFormChange('fromColonyId', event.target.value)
                }
              >
                {galaxyState.colonies.map(colony => (
                  <option key={colony.id} value={colony.id}>
                    {colony.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              To Colony
              <select
                value={newRouteForm.toColonyId}
                onChange={event =>
                  handleTradeFormChange('toColonyId', event.target.value)
                }
              >
                {galaxyState.colonies.map(colony => (
                  <option key={colony.id} value={colony.id}>
                    {colony.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Resource
              <select
                value={newRouteForm.resource}
                onChange={event =>
                  handleTradeFormChange('resource', event.target.value)
                }
              >
                {RESOURCE_KEYS.map(resource => (
                  <option key={resource} value={resource}>
                    {RESOURCE_LABELS[resource]}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Capacity (units/year)
              <input
                type='number'
                min={2}
                max={60}
                step={1}
                value={newRouteForm.capacity}
                onChange={event =>
                  handleTradeFormChange('capacity', Number(event.target.value))
                }
              />
            </label>
          </div>
          <button
            type='button'
            className='space-mode__primary-button'
            onClick={handleCreateTradeRoute}
          >
            Establish Route
          </button>
        </div>
        <div className='space-mode__trade-routes'>
          {galaxyState.tradeRoutes.length === 0 ? (
            <p>
              No trade routes configured. Establish a route to begin
              inter-colony commerce.
            </p>
          ) : (
            galaxyState.tradeRoutes.map(route => {
              const fromColony = galaxyState.colonies.find(
                colony => colony.id === route.fromColonyId
              );
              const toColony = galaxyState.colonies.find(
                colony => colony.id === route.toColonyId
              );
              const efficiency = Math.min(
                1,
                route.efficiency + aggregateEffects.tradeEfficiencyBonus
              );

              return (
                <div key={route.id} className='space-mode__trade-row'>
                  <div>
                    <strong>
                      {fromColony?.name ?? route.fromColonyId} →{' '}
                      {toColony?.name ?? route.toColonyId}
                    </strong>
                    <p>
                      {RESOURCE_LABELS[route.resource]} • Capacity{' '}
                      {roundTo(route.capacity, 1)} • Effective Transfer{' '}
                      {roundTo(route.capacity * efficiency, 1)}
                    </p>
                  </div>
                  <div className='space-mode__trade-actions'>
                    <button
                      type='button'
                      onClick={() => handleAdjustTradeCapacity(route.id, -2)}
                    >
                      -
                    </button>
                    <button
                      type='button'
                      onClick={() => handleAdjustTradeCapacity(route.id, 2)}
                    >
                      +
                    </button>
                    <button
                      type='button'
                      onClick={() => handleRemoveTradeRoute(route.id)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </section>

      <section className='space-mode__section'>
        <h2>Galactic Ledger</h2>
        <div className='space-mode__log'>
          {galaxyState.logs.map((entry, index) => (
            <p key={`${entry}-${index}`}>{entry}</p>
          ))}
        </div>
      </section>
    </div>
  );
};
